// client/src/features/expenses/enums.ts — the expense wire enums, as runtime lists · EXP-005.
//
// Same construction as `features/contracts/enums.ts`, and for the same reason: the generated
// client gives each enum as a string-literal UNION, and a union has no runtime value — a
// `<select>` cannot iterate it and `z.enum()` cannot validate against it. Each list is spelled
// out once and HELD to its union by an exhaustive `Record<T, true>`, so a member EXP-001 or
// EXP-004 adds and this file lacks — or one this file keeps after the contract dropped it — is
// a compile error in the PR that regenerated the client.
//
// Labels stay in `romanian.json` under `enums.{EnumName}.{VALUE}` (04 §9.6.3).

import type {
  ContractPeriodicity,
  DeductibilityStatus,
  ExpenseStatus,
  InvoiceProcessingStatus,
  RecurringAmountType,
} from '@/client';

/** 03 §9.3's three, in the order a cost walks them. */
const EXPENSE_STATUS_ORDER: Record<ExpenseStatus, true> = {
  PLANNED: true,
  PAID: true,
  CANCELLED: true,
};

export const EXPENSE_STATUSES = Object.keys(EXPENSE_STATUS_ORDER) as readonly ExpenseStatus[];

/**
 * The fiscal verdict. Every row reads `UNKNOWN` for the whole of V1 — FISC-006 (M4) is what
 * moves it, and no request type carries it (D-680) — but the list is exhaustive over the
 * generated union so the badge's colour table cannot fall short of the contract.
 */
const DEDUCTIBILITY_ORDER: Record<DeductibilityStatus, true> = {
  DEDUCTIBLE: true,
  NON_DEDUCTIBLE: true,
  UNKNOWN: true,
};

export const DEDUCTIBILITY_STATUSES = Object.keys(
  DEDUCTIBILITY_ORDER,
) as readonly DeductibilityStatus[];

/**
 * 03 §9.6's five. Four of them are the OCR pipeline's and are written by nothing in V1: a
 * manually typed invoice is `CONFIRMED` by whoever typed it and `status` is on no request type
 * (D-807). The list exists so the register's chip can label whatever the wire says rather than
 * assume the one value V1 produces.
 */
const INVOICE_STATUS_ORDER: Record<InvoiceProcessingStatus, true> = {
  UPLOADED: true,
  OCR_PENDING: true,
  OCR_DONE: true,
  OCR_FAILED: true,
  CONFIRMED: true,
};

export const INVOICE_PROCESSING_STATUSES = Object.keys(
  INVOICE_STATUS_ORDER,
) as readonly InvoiceProcessingStatus[];

/**
 * The three statuses the OCR pipeline owns — `InvoiceRules.OcrStatuses` on the server. A row in
 * one of them is a reading awaiting a human, and the register leads it back to its confirm
 * screen (D-2614).
 */
const OCR_STATUSES: ReadonlySet<InvoiceProcessingStatus> = new Set<InvoiceProcessingStatus>([
  'OCR_PENDING',
  'OCR_DONE',
  'OCR_FAILED',
]);

/** Whether the invoice is a reading nobody has confirmed yet. */
export function isOcrStatus(status: InvoiceProcessingStatus): boolean {
  return OCR_STATUSES.has(status);
}

/**
 * The currencies a V1 cost can be recorded in. `CONTRACT_CURRENCIES` has the same three and the
 * same reason (spec §5): `app.currencies` is the server's list and a 422
 * (`expense-currency-unknown`) is the enforcement — this is the offer, not the rule.
 */
export const EXPENSE_CURRENCIES = ['RON', 'EUR', 'USD'] as const;

/** The default the server applies to an absent currency (`ExpenseRules.NormalizeCurrency`). */
export const DEFAULT_CURRENCY = 'RON';

/**
 * 03 §9.4's two: a FIXED amount is the obligation; an ESTIMATED one is the obligation with
 * D-694's note on every generated row, to be corrected when the invoice arrives. EXP-005
 * phase 2's builder is the first consumer.
 */
const RECURRING_AMOUNT_TYPE_ORDER: Record<RecurringAmountType, true> = {
  FIXED: true,
  ESTIMATED: true,
};

export const RECURRING_AMOUNT_TYPES = Object.keys(
  RECURRING_AMOUNT_TYPE_ORDER,
) as readonly RecurringAmountType[];

/**
 * The rule's cadence is the CONTRACT's periodicity type on the wire — 03 §9.4 reuses the enum
 * — so the labels live under `enums.ContractPeriodicity` already (CON-005) and this list only
 * holds the members in the order a builder offers them.
 */
const RECURRING_PERIODICITY_ORDER: Record<ContractPeriodicity, true> = {
  MONTHLY: true,
  QUARTERLY: true,
  ANNUAL: true,
};

export const RECURRING_PERIODICITIES = Object.keys(
  RECURRING_PERIODICITY_ORDER,
) as readonly ContractPeriodicity[];
